import { Col, message, Row, Space, Spin, Table, Tag } from "antd";
import { FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import React, { Component } from "react";
import { connect } from "react-redux";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import { ResponsivePie } from "@nivo/pie";
import { ButtonItem, ErrorItem, Form, TableItemDinamico } from "../../../../Components/Items";
import FiltrosCalidad from "./FiltrosCalidad";
import {
  descargarPdfCalidad,
  exportarExcel,
  fmtDuracion,
  fmtEjecutado,
  fmtFecha,
  fmtFraccion,
  fmtResultado,
} from "./exportCalidad";
import {
  ChartCard,
  COLOR_RESULTADO,
  COLORES_RESULTADO,
  colorPorcentaje,
  EstadoVacio,
  fmtDiaCorto,
  isMobile,
  KpiRow,
  NIVO_THEME,
  ordenarPorClave,
  RESULTADO_LABEL,
  RESULTADO_TAG,
  tagPorcentaje,
  TooltipCalidad,
} from "./reporteCalidadStyles";

const PERIODO_INICIAL = { periodo: [moment().subtract(1, "month"), moment()] };

const recortar = (texto, largo) =>
  texto && texto.length > largo ? `${texto.substring(0, largo)}…` : texto;

class ReportePrograma extends Component {
  formRef = React.createRef();
  tableRef = null;

  state = {
    loading: false,
    registros: [],
    totalRegistros: 0,
    resumen: null,
    programa: null,
    porProceso: [],
    porColaborador: [],
    peoresCriterios: [],
    empresa: null,
    currentFilters: {},
  };

  buildPayload(values = {}) {
    const payload = {};
    if (values.programaId) payload.programaId = values.programaId;
    if (values.periodo?.length === 2) {
      payload.fechaInicio = values.periodo[0].startOf("day").toISOString();
      payload.fechaFin = values.periodo[1].endOf("day").toISOString();
    }
    return payload;
  }

  onFetchData(params) {
    // El reporte no tiene sentido sin programa: se espera a que el usuario elija uno
    if (!params.programaId) return;

    this.setState({ loading: true });
    axios
      .post(
        "programa/reportePrograma",
        { empresaId: this.props.empresaId, ...params },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            registros: res.data.data,
            totalRegistros: res.data.totalRecords,
            resumen: res.data.resumen,
            programa: res.data.programa,
            porProceso: res.data.porProceso ?? [],
            porColaborador: res.data.porColaborador ?? [],
            peoresCriterios: res.data.peoresCriterios ?? [],
            empresa: res.data.empresa ?? null,
          });
        } else {
          message.error(res.data.message ?? "No se pudo generar el reporte");
        }
      })
      .catch(() => message.error("Error de conexión"))
      .finally(() => this.setState({ loading: false }));
  }

  consultarReporte(values) {
    const payload = this.buildPayload(values);
    this.setState({ currentFilters: payload });
    this.tableRef?.updateExternalFilters(payload);
  }

  async fetchAll() {
    const res = await axios.post(
      "programa/reportePrograma",
      { empresaId: this.props.empresaId, ...this.state.currentFilters },
      { headers: this.props.headersToken }
    );
    if (res.data.success !== 2) throw new Error("Error al obtener datos");
    return res.data;
  }

  async descargarExcel() {
    this.setState({ loading: true });
    try {
      const { data: registros, resumen } = await this.fetchAll();
      exportarExcel(
        "Reporte_Programa_Calidad",
        "Programa",
        registros.map((r) => ({
          Proceso: r.proceso?.nombre ?? "-",
          "Ejecutado por": fmtEjecutado(r),
          Resultado: fmtResultado(r.resultado),
          "Fecha inicio": fmtFecha(r.fechaInicio),
          "Fecha fin": fmtFecha(r.fechaFin),
          "Duración (min)": r.duracionMinutos ?? "-",
          ...(resumen?.usaPuntos
            ? { Puntos: fmtFraccion(r.puntosCompletados, r.puntosTotales) }
            : {}),
          ...(resumen?.usaPasos ? { Pasos: fmtFraccion(r.pasosCompletados, r.pasosTotales) } : {}),
          ...(resumen?.usaCriterios
            ? { Criterios: fmtFraccion(r.criteriosCumplidos, r.criteriosTotales) }
            : {}),
          "% Cumplimiento": r.porcentajeCumplimiento != null ? `${r.porcentajeCumplimiento}%` : "-",
          Observaciones: r.observaciones || "",
        }))
      );
    } catch {
      message.error("No se pudo generar el Excel");
    } finally {
      this.setState({ loading: false });
    }
  }

  async descargarPdf() {
    this.setState({ loading: true });
    try {
      const {
        data: registros,
        resumen,
        empresa,
        programa,
        porProceso,
        porColaborador,
        peoresCriterios,
      } = await this.fetchAll();
      const { usaPuntos, usaPasos, usaCriterios } = resumen ?? {};

      const filasDesglose = (lista) =>
        lista.map((g) => [
          g.nombre,
          { text: `${g.total}`, alignment: "center" },
          { text: `${g.completados}`, alignment: "center" },
          { text: `${g.conObservaciones}`, alignment: "center" },
          { text: `${g.rechazados}`, alignment: "center" },
          { text: `${g.porcentajeCumplimiento}%`, alignment: "center", bold: true },
          { text: usaCriterios ? `${g.porcentajeCriterios}%` : "-", alignment: "center" },
          { text: fmtDuracion(g.duracionPromedioMinutos), alignment: "center" },
        ]);
      const columnasDesglose = [
        "Nombre",
        "Registros",
        "Compl.",
        "Con obs.",
        "Rechaz.",
        "% Cumpl.",
        "% Criterios",
        "Dur. prom.",
      ];
      const widthsDesglose = ["*", 56, 46, 52, 50, 56, 62, 58];

      descargarPdfCalidad({
        empresa,
        titulo: "Reporte por Programa",
        subtitulo: programa?.nombre ?? "-",
        extras: [
          `Cliente: ${programa?.cliente ?? "-"}`,
          `Supervisor: ${programa?.supervisor ?? "-"}`,
          `Total registros: ${registros.length}`,
        ],
        nombreArchivo: "Reporte_Programa_Calidad",
        kpis: [
          { label: "Total", value: resumen?.total ?? 0 },
          { label: "Completados", value: resumen?.completados ?? 0 },
          { label: "Con obs.", value: resumen?.conObservaciones ?? 0 },
          { label: "Rechazados", value: resumen?.rechazados ?? 0 },
          { label: "% Cumplimiento", value: `${resumen?.porcentajeCumplimiento ?? 0}%` },
          usaPuntos && { label: "% Puntos", value: `${resumen?.porcentajePuntos ?? 0}%` },
          usaCriterios && { label: "% Criterios", value: `${resumen?.porcentajeCriterios ?? 0}%` },
          {
            label: "Dur. promedio",
            value:
              resumen?.duracionPromedioMinutos != null
                ? `${resumen.duracionPromedioMinutos} min`
                : "-",
          },
        ],
        secciones: [
          {
            titulo: "Datos del programa",
            columnas: ["Programa", "Cliente", "Proyecto", "Supervisor", "Colaboradores"],
            widths: ["*", "*", "*", "*", "*"],
            filas: [
              [
                programa?.nombre ?? "-",
                programa?.cliente ?? "-",
                programa?.proyecto ?? "-",
                programa?.supervisor ?? "-",
                (programa?.colaboradores ?? []).map((c) => `${c.nombre} (${c.rol})`).join(", ") ||
                  "-",
              ],
            ],
          },
          {
            titulo: `Desglose por proceso (${porProceso.length})`,
            columnas: columnasDesglose,
            widths: widthsDesglose,
            filas: filasDesglose(porProceso),
          },
          {
            titulo: `Desglose por colaborador (${porColaborador.length})`,
            columnas: columnasDesglose,
            widths: widthsDesglose,
            filas: filasDesglose(porColaborador),
            vacio: "Ningún registro tiene colaborador identificado.",
          },
          {
            titulo: "Criterios que más fallan en el programa",
            columnas: ["Criterio", "Punto de control", "Evaluaciones", "No cumplidos", "%"],
            widths: ["*", "*", 62, 62, 34],
            filas: (peoresCriterios ?? []).map((c) => [
              c.criterio,
              c.punto,
              { text: `${c.evaluaciones}`, alignment: "center" },
              { text: `${c.noCumplidos}`, alignment: "center" },
              { text: `${c.porcentaje}%`, alignment: "center", bold: true },
            ]),
            vacio: "Todos los criterios del programa se cumplieron al 100%.",
          },
          {
            titulo: `Registros (${registros.length})`,
            columnas: [
              "Proceso",
              "Ejecutado por",
              "Resultado",
              "Inicio",
              "Fin",
              "Duración",
              ...(usaPuntos ? ["Puntos"] : []),
              ...(usaPasos ? ["Pasos"] : []),
              ...(usaCriterios ? ["Criterios"] : []),
            ],
            widths: [
              "*", "*", 60, 50, 50, 38,
              ...(usaPuntos ? [40] : []),
              ...(usaPasos ? [40] : []),
              ...(usaCriterios ? [46] : []),
            ],
            filas: registros.map((r) => [
              r.proceso?.nombre ?? "-",
              fmtEjecutado(r),
              { text: fmtResultado(r.resultado), bold: true },
              fmtFecha(r.fechaInicio),
              fmtFecha(r.fechaFin),
              { text: fmtDuracion(r.duracionMinutos), alignment: "center" },
              ...(usaPuntos
                ? [{ text: fmtFraccion(r.puntosCompletados, r.puntosTotales), alignment: "center" }]
                : []),
              ...(usaPasos
                ? [{ text: fmtFraccion(r.pasosCompletados, r.pasosTotales), alignment: "center" }]
                : []),
              ...(usaCriterios
                ? [
                    {
                      text: fmtFraccion(r.criteriosCumplidos, r.criteriosTotales),
                      alignment: "center",
                    },
                  ]
                : []),
            ]),
          },
        ],
      });
    } catch {
      message.error("No se pudo generar el PDF");
    } finally {
      this.setState({ loading: false });
    }
  }

  kpis() {
    const { resumen } = this.state;
    if (!resumen) return [];
    return [
      { key: "total", label: "Registros", value: resumen.total, color: "#0f172a" },
      { key: "completados", label: "Completados", value: resumen.completados, color: COLOR_RESULTADO.completado },
      { key: "conObs", label: "Con obs.", value: resumen.conObservaciones, color: COLOR_RESULTADO.completado_con_observaciones },
      { key: "rechazados", label: "Rechazados", value: resumen.rechazados, color: COLOR_RESULTADO.rechazado },
      {
        key: "cumplimiento",
        label: "% Cumplimiento",
        value: `${resumen.porcentajeCumplimiento}%`,
        color: colorPorcentaje(resumen.porcentajeCumplimiento),
      },
      resumen.usaPuntos && {
        key: "puntos",
        label: "% Puntos compl.",
        value: `${resumen.porcentajePuntos}%`,
        color: "#0ea5e9",
        hint: `${resumen.totalPuntosCompletados}/${resumen.totalPuntos}`,
      },
      resumen.usaCriterios && {
        key: "criterios",
        label: "% Criterios",
        value: `${resumen.porcentajeCriterios}%`,
        color: "#6366f1",
        hint: `${resumen.totalCriteriosCumplidos}/${resumen.totalCriterios}`,
      },
      resumen.duracionPromedioMinutos != null && {
        key: "duracion",
        label: "Duración prom.",
        value: `${resumen.duracionPromedioMinutos} min`,
        color: "#8b5cf6",
      },
    ].filter(Boolean);
  }

  ficha() {
    const { programa } = this.state;
    if (!programa) return null;
    const datos = [
      { label: "Cliente", valor: programa.cliente },
      { label: "Proyecto", valor: programa.proyecto },
      { label: "Supervisor", valor: programa.supervisor },
      { label: "Fecha", valor: programa.fecha ? moment(programa.fecha).format("DD/MM/YYYY") : "-" },
      { label: "Estado", valor: programa.estado === 1 ? "Activo" : "Deshabilitado" },
      {
        label: "Colaboradores",
        valor:
          programa.colaboradores?.length > 0
            ? programa.colaboradores.map((c) => `${c.nombre} (${c.rol})`).join(", ")
            : "-",
      },
    ];

    return (
      <div className="cal-ficha">
        <div className="cal-ficha-nombre">{programa.nombre}</div>
        <Row gutter={[16, 8]}>
          {datos.map((d) => (
            <Col key={d.label} xs={24} sm={12} md={8}>
              <div className="cal-ficha-dato">
                {d.label}: <b>{d.valor || "-"}</b>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  graficas() {
    const { resumen, porProceso } = this.state;
    if (!resumen || resumen.total === 0) return null;

    const movil = isMobile();

    const dataPie = [
      { id: "Completado", label: "Completado", value: resumen.completados },
      { id: "Con obs.", label: "Con obs.", value: resumen.conObservaciones },
      { id: "Rechazado", label: "Rechazado", value: resumen.rechazados },
      { id: "Pendiente", label: "Pendiente", value: resumen.pendientes },
    ].filter((d) => d.value > 0);

    const dataProcesos = porProceso.slice(0, 12).map((g) => ({
      proceso: recortar(g.nombre, movil ? 10 : 16),
      procesoCompleto: g.nombre,
      Completado: g.completados,
      "Con obs.": g.conObservaciones,
      Rechazado: g.rechazados,
      Pendiente: g.pendientes,
      cumplimiento: g.porcentajeCumplimiento,
    }));

    const dataLine = Object.entries(resumen.diasMap ?? {})
      .sort(ordenarPorClave)
      .map(([clave, { suma, n }]) => ({ x: fmtDiaCorto(clave), y: Math.round(suma / n) }));

    return (
      <>
        <Row gutter={[14, 14]} style={{ marginBottom: 14 }}>
          {dataPie.length > 0 && (
            <Col xs={24} md={9}>
              <ChartCard
                label="Resultados"
                titulo="Distribución del programa"
                pill={`${resumen.total} registros`}
                delay={0.06}
                altura={250}
              >
                <ResponsivePie
                  data={dataPie}
                  margin={
                    movil
                      ? { top: 10, right: 10, bottom: 40, left: 10 }
                      : { top: 10, right: 20, bottom: 46, left: 20 }
                  }
                  colors={COLORES_RESULTADO}
                  innerRadius={0.67}
                  padAngle={1.8}
                  cornerRadius={6}
                  activeOuterRadiusOffset={9}
                  borderWidth={0}
                  enableArcLabels={false}
                  arcLinkLabelsSkipAngle={movil ? 360 : 14}
                  arcLinkLabelsTextColor="#94a3b8"
                  arcLinkLabelsThickness={1}
                  arcLinkLabelsColor={{ from: "color" }}
                  theme={NIVO_THEME}
                  legends={[
                    {
                      anchor: "bottom",
                      direction: "row",
                      translateY: 42,
                      itemsSpacing: 6,
                      itemWidth: movil ? 68 : 82,
                      itemHeight: 16,
                      itemTextColor: "#64748b",
                      symbolSize: 9,
                      symbolShape: "circle",
                    },
                  ]}
                  tooltip={({ datum }) => (
                    <TooltipCalidad
                      color={datum.color}
                      titulo={datum.label}
                      filas={[{ label: "Registros", value: datum.value }]}
                    />
                  )}
                />
              </ChartCard>
            </Col>
          )}

          {dataProcesos.length > 0 && (
            <Col xs={24} md={dataPie.length ? 15 : 24}>
              <ChartCard
                label="Desglose"
                titulo="Resultados por proceso"
                pill={`${porProceso.length} procesos`}
                accent="linear-gradient(90deg, #0ea5e9, #38bdf8)"
                delay={0.12}
                altura={250}
              >
                <ResponsiveBar
                  data={dataProcesos}
                  keys={["Completado", "Con obs.", "Rechazado", "Pendiente"]}
                  indexBy="proceso"
                  margin={{ top: 10, right: 8, bottom: movil ? 76 : 66, left: 40 }}
                  padding={0.28}
                  innerPadding={2}
                  groupMode="stacked"
                  colors={COLORES_RESULTADO}
                  colorBy="id"
                  borderRadius={4}
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{ tickSize: 0, tickPadding: 10, tickRotation: -35 }}
                  axisLeft={{ tickSize: 0, tickPadding: 8 }}
                  enableGridX={false}
                  gridYValues={4}
                  enableLabel={false}
                  theme={NIVO_THEME}
                  tooltip={({ id, value, color, data }) => (
                    <TooltipCalidad
                      color={color}
                      titulo={data.procesoCompleto}
                      filas={[
                        { label: id, value },
                        { label: "% Cumplimiento", value: `${data.cumplimiento}%` },
                      ]}
                    />
                  )}
                  legends={
                    movil
                      ? []
                      : [
                          {
                            dataFrom: "keys",
                            anchor: "bottom",
                            direction: "row",
                            translateY: 60,
                            itemWidth: 96,
                            itemHeight: 16,
                            itemsSpacing: 4,
                            symbolSize: 8,
                            symbolShape: "circle",
                            itemTextColor: "#64748b",
                          },
                        ]
                  }
                />
              </ChartCard>
            </Col>
          )}
        </Row>

        {dataLine.length > 1 && (
          <Row style={{ marginBottom: 14 }}>
            <Col span={24}>
              <ChartCard
                label="Evolución"
                titulo="% de cumplimiento por día"
                accent="linear-gradient(90deg, #6366f1, #a78bfa)"
                delay={0.18}
                altura={210}
              >
                <ResponsiveLine
                  data={[{ id: "% Cumplimiento", data: dataLine }]}
                  margin={{ top: 10, right: 20, bottom: 54, left: 46 }}
                  xScale={{ type: "point" }}
                  yScale={{ type: "linear", min: 0, max: 100 }}
                  curve="monotoneX"
                  colors={["#6366f1"]}
                  lineWidth={2.5}
                  pointSize={6}
                  pointColor="#ffffff"
                  pointBorderWidth={2}
                  pointBorderColor={{ from: "serieColor" }}
                  enableArea
                  areaOpacity={0.08}
                  enableSlices="x"
                  enableGridX={false}
                  gridYValues={[0, 25, 50, 75, 100]}
                  theme={NIVO_THEME}
                  axisBottom={{
                    tickSize: 0,
                    tickPadding: 10,
                    tickRotation: -30,
                    tickValues: dataLine
                      .map((d) => d.x)
                      .filter((_, i) => i % Math.ceil(dataLine.length / (movil ? 6 : 12)) === 0),
                  }}
                  axisLeft={{ tickSize: 0, tickPadding: 8, format: (v) => `${v}%` }}
                  sliceTooltip={({ slice }) => (
                    <TooltipCalidad
                      color="#6366f1"
                      titulo={slice.points[0]?.data.x}
                      filas={[{ label: "Cumplimiento", value: `${slice.points[0]?.data.y}%` }]}
                    />
                  )}
                />
              </ChartCard>
            </Col>
          </Row>
        )}
      </>
    );
  }

  /** Tablas de desglose por proceso y por colaborador (client-side, ya vienen completas). */
  desgloses() {
    const { resumen, porProceso, porColaborador } = this.state;
    if (!resumen || resumen.total === 0) return null;

    const columnas = (tituloPrimera) =>
      [
        { title: tituloPrimera, dataIndex: "nombre", ellipsis: true },
        { title: "Registros", dataIndex: "total", align: "center", width: 90 },
        { title: "Compl.", dataIndex: "completados", align: "center", width: 80 },
        { title: "Con obs.", dataIndex: "conObservaciones", align: "center", width: 85 },
        { title: "Rechaz.", dataIndex: "rechazados", align: "center", width: 80 },
        {
          title: "% Cumpl.",
          dataIndex: "porcentajeCumplimiento",
          align: "center",
          width: 95,
          render: (v) => <Tag color={tagPorcentaje(v)}>{v}%</Tag>,
        },
        resumen.usaCriterios && {
          title: "% Criterios",
          dataIndex: "porcentajeCriterios",
          align: "center",
          width: 105,
          render: (v) => <Tag color={tagPorcentaje(v)}>{v}%</Tag>,
        },
        {
          title: "Dur. prom.",
          dataIndex: "duracionPromedioMinutos",
          align: "center",
          width: 100,
          render: fmtDuracion,
        },
      ].filter(Boolean);

    return (
      <Row gutter={[14, 14]} style={{ marginBottom: 14 }}>
        <Col xs={24} xl={12}>
          <div className="cal-card" style={{ animationDelay: "0.22s" }}>
            <div className="cal-card-head">
              <div>
                <div className="cal-card-label">Desglose</div>
                <h2 className="cal-card-title">Por proceso</h2>
              </div>
            </div>
            <div className="cal-card-accent" />
            <Table
              size="small"
              rowKey="id"
              dataSource={porProceso}
              columns={columnas("Proceso")}
              pagination={porProceso.length > 8 ? { pageSize: 8, size: "small" } : false}
              scroll={{ x: "max-content" }}
            />
          </div>
        </Col>
        <Col xs={24} xl={12}>
          <div className="cal-card" style={{ animationDelay: "0.26s" }}>
            <div className="cal-card-head">
              <div>
                <div className="cal-card-label">Desglose</div>
                <h2 className="cal-card-title">Por colaborador</h2>
              </div>
            </div>
            <div
              className="cal-card-accent"
              style={{ background: "linear-gradient(90deg, #f59e0b, #fbbf24)" }}
            />
            <Table
              size="small"
              rowKey="id"
              dataSource={porColaborador}
              columns={columnas("Colaborador")}
              locale={{ emptyText: "Ningún registro tiene colaborador identificado" }}
              pagination={porColaborador.length > 8 ? { pageSize: 8, size: "small" } : false}
              scroll={{ x: "max-content" }}
            />
          </div>
        </Col>
      </Row>
    );
  }

  columnasRegistros() {
    const { resumen } = this.state;
    return [
      { title: "Proceso", key: "proceso", render: (_, r) => r.proceso?.nombre ?? "-", ellipsis: true },
      { title: "Ejecutado por", key: "ejecutadoPor", render: (_, r) => fmtEjecutado(r), ellipsis: true },
      {
        title: "Resultado",
        dataIndex: "resultado",
        width: 140,
        render: (v) => <Tag color={RESULTADO_TAG[v] ?? "default"}>{RESULTADO_LABEL[v] ?? v}</Tag>,
      },
      { title: "Inicio", dataIndex: "fechaInicio", render: fmtFecha, width: 120 },
      { title: "Fin", dataIndex: "fechaFin", render: fmtFecha, width: 120 },
      {
        title: "Duración",
        dataIndex: "duracionMinutos",
        render: fmtDuracion,
        align: "center",
        width: 90,
      },
      resumen?.usaPuntos && {
        title: "Puntos",
        key: "puntos",
        align: "center",
        width: 85,
        render: (_, r) => fmtFraccion(r.puntosCompletados, r.puntosTotales),
      },
      resumen?.usaCriterios && {
        title: "Criterios ✓",
        key: "criterios",
        align: "center",
        width: 100,
        render: (_, r) => fmtFraccion(r.criteriosCumplidos, r.criteriosTotales),
      },
      {
        title: "% Cumpl.",
        dataIndex: "porcentajeCumplimiento",
        align: "center",
        width: 95,
        render: (v) => (v != null ? <Tag color={tagPorcentaje(v)}>{v}%</Tag> : "-"),
      },
    ].filter(Boolean);
  }

  render() {
    const { registros, totalRegistros, resumen, loading, currentFilters } = this.state;
    const sinPrograma = !currentFilters.programaId;

    return (
      <Form
        ref={this.formRef}
        name="reporteCalidadPrograma"
        onFinish={this.consultarReporte.bind(this)}
        onFinishFailed={ErrorItem}
        initialValues={PERIODO_INICIAL}
      >
        <Spin spinning={loading} wrapperClassName="cal-spin">
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <Space>
              {resumen && !sinPrograma && (
                <ButtonItem verde onClick={() => this.descargarExcel()} icon={<FileExcelOutlined />}>
                  Excel
                </ButtonItem>
              )}
              {resumen && !sinPrograma && (
                <ButtonItem rojo onClick={() => this.descargarPdf()} icon={<FilePdfOutlined />}>
                  PDF
                </ButtonItem>
              )}
              <ButtonItem type="primary" htmlType="submit">
                Consultar
              </ButtonItem>
            </Space>
          </div>

          <FiltrosCalidad
            campos={["periodo", "programa"]}
            requeridos={["programaId"]}
            formRef={this.formRef}
          />

          {sinPrograma && (
            <EstadoVacio
              titulo="Elige un programa"
              texto="Selecciona un programa y pulsa Consultar para ver su desempeño por proceso y por colaborador."
            />
          )}

          {!sinPrograma && (
            <>
              {this.ficha()}
              <KpiRow items={this.kpis()} />
              {this.graficas()}
              {this.desgloses()}
              {resumen && resumen.total === 0 && (
                <EstadoVacio
                  titulo="Sin registros en el período"
                  texto="Este programa no tiene registros de proceso en el rango de fechas seleccionado."
                />
              )}
            </>
          )}

          {/*
            La tabla se monta siempre: TableItemDinamico expone el ref con el que
            updateExternalFilters dispara la consulta, así que desmontarla rompería el filtro.
            Mientras no haya programa (o no haya registros) solo se oculta.
          */}
          <div
            style={{
              display: sinPrograma || (resumen && resumen.total === 0) ? "none" : "block",
            }}
          >
            <TableItemDinamico
              onRef={(ref) => (this.tableRef = ref)}
              totalRecords={totalRegistros}
              onFetchData={this.onFetchData.bind(this)}
              loading={loading}
              size="small"
              dataSource={registros}
              columns={this.columnasRegistros()}
              rowKey={(r) => r._id?.toString()}
            />
          </div>
        </Spin>
      </Form>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ReportePrograma);
